import { ORPCError } from "@orpc/client";
import { useRef } from "react";
import { noteHash } from "@/lib/noteHash";
import { referenceClient } from "@/lib/orpc/client";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
} from "@/lib/visit-queries";

export type RestoreOutcome = "restored" | "stale" | "error";

export interface NoteRestore {
  visitPublicId: string;
  // The visit's clinical note (null) or another note of the visit.
  notePublicId: string | null;
  // The text to bring back, and the text the note should have now.
  restore: string;
  expected: string;
}

// The current text of one of the visit's other notes (letters, summaries,
// another generation), read from the API.
const currentNoteText = async (
  visitPublicId: string,
  notePublicId: string,
  uid: string | undefined,
): Promise<string | null | undefined> => {
  let cursor: string | undefined;
  for (let page = 0; page < 20; page++) {
    const value = await referenceClient.listVisitNotes(
      {
        params: { public_id: visitPublicId },
        query: { limit: "100", ...(cursor ? { cursor } : {}) },
      },
      { context: { expectedUserUid: uid } },
    );
    const note = value.items.find((item) => item.public_id === notePublicId);
    if (note) return note.result;
    if (!value.nextCursor) return undefined;
    cursor = value.nextCursor;
  }
  return undefined;
};

// Puts a note back to an earlier text (Undo of an assistant edit, Restore in
// the version history), only while the note is still the text the
// clinician saw. The clinical note goes through the API's restore, which
// keeps the replaced note in the version history; another note of the visit
// (no version history) is saved back after checking its current text.
export const useNoteRestore = () => {
  const scope = useVisitScope();
  const busy = useRef(false);
  return async (request: NoteRestore): Promise<RestoreOutcome> => {
    if (!scope || busy.current) return "error";
    busy.current = true;
    const context = { context: { expectedUserUid: scope.uid } };
    try {
      if (request.notePublicId === null) {
        await referenceClient.restoreVisitNote(
          {
            params: { public_id: request.visitPublicId },
            query: {},
            body: {
              restoreVersion: await noteHash(request.restore),
              expectedVersion: await noteHash(request.expected),
            },
          },
          context,
        );
      } else {
        const current = await currentNoteText(
          request.visitPublicId,
          request.notePublicId,
          scope.uid,
        );
        if (current !== request.expected) return "stale";
        await referenceClient.updateVisitNote(
          {
            params: {
              public_id: request.visitPublicId,
              notePublicId: request.notePublicId,
            },
            body: { result: request.restore },
          },
          context,
        );
      }
      return "restored";
    } catch (error) {
      return error instanceof ORPCError && error.code === "CONFLICT"
        ? "stale"
        : "error";
    } finally {
      busy.current = false;
      if (getVisitScope() === scope) void refreshVisitReads(scope);
    }
  };
};
